import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { AiFillClockCircle, AiOutlineArrowRight } from "react-icons/ai";
import { BiSolidMessageAlt } from "react-icons/bi";

interface HealthPost {
  id: number;
  name: string;
  avatar: string;
  description: string;
  tag: string;
}

/* Dummy posts shown when the API is not reachable, so the section
   always renders rich content and imagery. */
const dummyHealths: HealthPost[] = [
  {
    id: 1,
    name: "Morning habits that boost your energy all day",
    avatar: "/blog/health.png",
    description:
      "Small, science-backed routines in the first hour of your day — hydration, sunlight and movement — can dramatically improve your focus and mood.",
    tag: "Health",
  },
  {
    id: 2,
    name: "The truth about superfoods you should know",
    avatar: "/blog/latest-news.png",
    description:
      "From blueberries to kale, we break down which 'superfoods' really earn their reputation and how to eat for long-term wellness.",
    tag: "Nutrition",
  },
  {
    id: 3,
    name: "5 simple exercises for a healthier heart",
    avatar: "/blog/latest-1.png",
    description:
      "You don't need a gym to keep your heart strong. These five daily movements fit into any schedule and deliver lasting cardiovascular benefits.",
    tag: "Fitness",
  },
  {
    id: 4,
    name: "Why good sleep is your body's best medicine",
    avatar: "/blog/latest-2.png",
    description:
      "Quality rest repairs tissues, balances hormones and sharpens memory. Learn how to build a bedtime ritual that actually works.",
    tag: "Wellness",
  },
];

const API_BASE = "https://localhost:7246/";

/* Resolve API-relative paths to absolute, while keeping plain asset paths. */
const toImage = (src: string) =>
  src.startsWith("http") || src.startsWith("/") ? src : API_BASE + src;

export default function Health() {
  const [healths, setHealths] = useState<HealthPost[]>(dummyHealths);

  useEffect(() => {
    const fetchHealthData = async () => {
      try {
        const response = await fetch(`${API_BASE}api/Healths`);
        const data = await response.json();
        if (data && data.result?.length) {
          setHealths(data.result);
        }
      } catch (error) {
        // Keep the dummy data when the backend is unreachable.
        console.warn("Health API unavailable, showing sample posts:", error);
      }
    };

    fetchHealthData();
  }, []);

  return (
    <div className="my-10">
      <div className="bg-white py-5 md:px-12 px-4 flex justify-between items-center rounded-lg shadow-sm">
        <h2 className="font-semibold capitalize text-xl md:text-3xl relative">
          <span>Health &amp; Bio</span>
          <div className="absolute -bottom-4 left-0 h-[6px] w-full bg-secondary"></div>
        </h2>
        <p className="text-primary text-xs md:text-base hover:underline cursor-pointer">
          View more Articles
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-8">
        {healths.map((post) => (
          <Link
            to="/blog-details"
            key={post.id}
            className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col sm:flex-row"
          >
            <div className="relative sm:w-2/5 overflow-hidden">
              <img
                className="h-44 sm:h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                src={toImage(post.avatar)}
                alt={post.name}
              />
              <span className="absolute top-3 left-3 px-3 py-1 text-xs font-semibold rounded-full bg-secondary text-white shadow">
                {post.tag}
              </span>
            </div>
            <div className="p-5 flex flex-col gap-3 flex-1">
              <h3 className="font-semibold text-base md:text-lg leading-snug line-clamp-2 group-hover:text-primary transition-colors">
                {post.name}
              </h3>
              <div className="flex items-center gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1.5">
                  <AiFillClockCircle /> 1 week ago
                </span>
                <span className="flex items-center gap-1.5">
                  <BiSolidMessageAlt /> 67
                </span>
              </div>
              <p className="text-sm text-gray-600 line-clamp-2">{post.description}</p>
              <span className="mt-auto inline-flex items-center gap-2 text-sm font-semibold text-primary">
                Continue Reading
                <AiOutlineArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}